import Form from "../../Helpers/Form/Form";
import { Offcanvas } from "react-bootstrap";
import FormHeader from "../../Helpers/Form/FormParts/FormHeader";
import trans, { isRTL } from "../../../utils";
import CompanyFormBody from "./CompanyFormBody";
import {
  useCreateCompanyMutation,
  useEditCompanyMutation,
  useGetSpecificCompanyQuery,
  useDeleteCompanyAttachmentsMutation,
} from "../../../Redux/service/Companies/Company";
import { Formik, Form as FormFormik } from "formik";
import Button from "../../Helpers/Button/Button";
import * as Yup from "yup";
import { useContext, useEffect } from "react";
import { popupcontext } from "../../../context/Popupscontext";
import { toast } from "react-toastify";

export default function FormCompany({
  companyForm,
  setCompanyForm,
  isEditing,
}) {
  const { setEdited, attachmentsId, setAttachmentsId, handleClose } = useContext(popupcontext);
  const { refetch, data: editData } = useGetSpecificCompanyQuery(companyForm, {
    skip: !isEditing || !companyForm,
  });
  const [createCompany] = useCreateCompanyMutation();
  const [editCompany] = useEditCompanyMutation();
  const [deleteAttachment] = useDeleteCompanyAttachmentsMutation();

  useEffect(() => {
    if (isEditing && companyForm) {
      refetch();
    }
  }, [isEditing, companyForm, refetch]);

  let initialValues = {};
  if (isEditing && editData?.data) {
    const data = editData?.data;
    initialValues = {
      code: data?.code || "",
      name: data?.name || "",
      licenseNumber: data?.licenseNumber || "",
      trade: data?.trade || [],
      contactPerson: data?.contactPerson || "",
      contactEmail: data?.contactEmail || "",
      contactPhone: data?.contactPhone || "",
      address: data?.address || "",
      status: data?.status || "ACTIVE",
      attachments: null,
    };
  } else {
    initialValues = {
      code: "",
      name: "",
      licenseNumber: "",
      trade: [],
      contactPerson: "",
      contactEmail: "",
      contactPhone: "",
      address: "",
      status: "ACTIVE",
      attachments: null,
    };
  }

  const validationSchema = Yup.object({
    code: Yup.string()
      .required(trans("Company code is required"))
      .matches(/^[^\s]/, trans("cannot start with a space")),
    name: Yup.string()
      .required(trans("Company name is required"))
      .matches(/^[^\s]/, trans("cannot start with a space")),
    licenseNumber: Yup.string()
      .nullable()
      .notRequired()
      .test(
        "no-space-start",
        trans("cannot start with a space"),
        function (value) {
          if (!value || value.length === 0) return true;
          return /^[^\s]/.test(value);
        }
      ),
    contactEmail: Yup.string()
      .nullable()
      .notRequired()
      .email(trans("Invalid email format")),
    status: Yup.string().required(trans("Status is required")),
  });

  const handleFormSubmit = async (values, setSubmitting, resetForm) => {
    setSubmitting(true);

    const payload = {
      ...values,
    };

    try {
      if (!isEditing && companyForm) {
        await createCompany(payload).unwrap();
        toast.success(trans("Company created successfully"), {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      } else {
        await editCompany({
          id: companyForm,
          payload: payload,
        }).unwrap();
        
        // Delete attachments that were marked for deletion
        if (attachmentsId && attachmentsId.length > 0) {
          try {
            await Promise.all(
              attachmentsId.map(async (id) => {
                await deleteAttachment(id).unwrap();
              })
            );
          } catch (error) {
            console.error("Error deleting attachments:", error);
            // Continue even if attachment deletion fails
          }
          // Clear attachmentsId after deletion
          setAttachmentsId([]);
        }
        
        toast.success(trans("Company edited successfully"), {
          autoClose: 800,
          rtl: localStorage.getItem("lang") === "ar" ? true : false,
        });
      }

      resetForm();
      setCompanyForm(false);
      setEdited(true);
      handleClose("form");
    } catch (err) {
      let errorMsg = trans(err?.data?.message ?? "Something went wrong");
      toast.error(errorMsg, {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
    } finally {
      setSubmitting(false);
      handleClose("form");
    }
  };

  return (
    <>
      <Form show={companyForm}>
        <Offcanvas.Header>
          <FormHeader
            title={
              !isEditing ? trans("Add new company") : trans("Edit Company")
            }
            other={false}
            onClose={() => {
              setCompanyForm(false);
              handleClose("form");
            }}
          />
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Formik
            initialValues={initialValues}
            enableReinitialize={true}
            onSubmit={(values, { setSubmitting, resetForm }) =>
              handleFormSubmit(values, setSubmitting, resetForm)
            }
            validationSchema={validationSchema}
          >
            {({ handleSubmit, isSubmitting }) => (
              <FormFormik onSubmit={handleSubmit} dir={!isRTL ? "ltr" : "rtl"}>
                <CompanyFormBody isEditing={isEditing} editData={editData} />
                <div className="offcanvas-footer m-0">
                  <div className="offcanvas-footer-body gap-8">
                    <Button
                      type={"primary"}
                      htmlType="submit"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? trans("Saving...") : trans("Save")}
                    </Button>
                    <Button
                      type={"default"}
                      htmlType="button"
                      disabled={isSubmitting}
                      onClick={() => {
                        setCompanyForm(false);
                        handleClose("form");
                      }}
                    >
                      {trans("Cancel")}
                    </Button>
                  </div>
                </div>
              </FormFormik>
            )}
          </Formik>
        </Offcanvas.Body>
      </Form>
    </>
  );
}
